/**
 * Stripe Billing Portal Integration
 * Production-ready implementation for billing portal access
 */

import { supabase } from '@/integrations/supabase/client';

interface BillingPortalResponse {
  url: string;
  customerId: string;
  returnUrl: string;
  sessionId: string;
}

/**
 * Create a Stripe billing portal session
 * This function handles the production-ready billing portal creation
 */
export const createBillingPortalSession = async (): Promise<BillingPortalResponse> => {
  try {
    // Get authenticated user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Get user profile with Stripe customer ID
    const { data: profile } = await supabase
      .from('profiles')
      .select('stripe_customer_id, subscription_status, email')
      .eq('id', user.id)
      .single();

    if (!profile?.stripe_customer_id) {
      throw new Error('No billing information found. Please contact support.');
    }

    if (profile.subscription_status === 'free') {
      throw new Error('Free plan users do not have access to billing portal. Please upgrade to a paid plan first.');
    }

    console.log(`Creating billing portal session for customer: ${profile.stripe_customer_id}`);

    const returnUrl = `${window.location.origin}/portal/billing/plan`;
    
    // Production Implementation Options:
    
    // Option 1: Call your backend API (Recommended for production)
    if (import.meta.env.VITE_API_BASE_URL) {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/stripe/billing-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await supabase.auth.getSession().then(s => s.data.session?.access_token)}`,
        },
        body: JSON.stringify({
          customerId: profile.stripe_customer_id,
          returnUrl: returnUrl,
        }),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('Backend billing portal error:', error);
        throw new Error('Failed to create billing portal session. Please try again.');
      }

      const session = await response.json();
      return {
        url: session.url,
        customerId: profile.stripe_customer_id,
        returnUrl: returnUrl,
        sessionId: session.id,
      };
    }
    
    // Option 2: Use Supabase Edge Function (Alternative)
    if (import.meta.env.VITE_SUPABASE_URL) { // Using Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('create-stripe-portal-session', {
        body: {
          customerId: profile.stripe_customer_id,
          returnUrl: returnUrl,
        },
      });

      if (error) {
        console.error('Supabase function error:', error);
        // Fall through to Option 3 instead of throwing error
      } else {
        return {
          url: data.url,
          customerId: profile.stripe_customer_id,
          returnUrl: returnUrl,
          sessionId: data.sessionId,
        };
      }
    }
    
    // Option 3: No fallback available - throw error
    throw new Error('Billing portal is temporarily unavailable. Please try again later or contact support.');

  } catch (error) {
    console.error('Error creating billing portal session:', error);
    throw error;
  }
};

/**
 * Alternative: Open billing portal in new tab
 * This can be used as a fallback if direct redirect doesn't work
 */
export const openBillingPortalInNewTab = async (): Promise<void> => {
  try {
    const session = await createBillingPortalSession();
    window.open(session.url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Error opening billing portal:', error);
    throw error;
  }
};

/**
 * Check if billing portal is available for user
 */
export const isBillingPortalAvailable = async (): Promise<boolean> => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) return false;

    const { data: profile } = await supabase
      .from('profiles')
      .select('stripe_customer_id, subscription_status')
      .eq('id', user.id)
      .single();

    return !!(profile?.stripe_customer_id && profile.subscription_status !== 'free');
  } catch (error) {
    console.error('Error checking billing portal availability:', error);
    return false;
  }
};

/**
 * Get billing portal configuration
 */
export const getBillingPortalConfig = () => {
  return {
    hasBackendAPI: !!import.meta.env.VITE_API_BASE_URL,
    hasSupabaseFunctions: !!import.meta.env.VITE_SUPABASE_URL,
    returnUrl: `${window.location.origin}/portal/billing/plan`,
    environment: import.meta.env.MODE,
  };
};
